import type { Cue, Voice } from "./cue.ts";

export type Note = {
  at: number;
  seconds: number;
  hz: number;

  toHz?: number;
  gain: number;
};

export type Shape = {
  wave: "sine" | "triangle";

  detune: number;
  attack: number;
  release: number;

  noise: boolean;

  filter: { kind: "highpass" | "bandpass"; of: number } | null;
};

export type Plan = { cue: Cue; voice: Voice; shape: Shape; notes: Note[] };

export const ROOT = 660;

export const SMALL = 2;
export const WIDE = 7;

export const AT_MOST = 0.22;

export const step = (semitones: number): number => ROOT * 2 ** (semitones / 12);

export const SHAPES: Record<Voice, Shape> = {
  human: { wave: "sine", detune: 0, attack: 0.018, release: 0.06, noise: false, filter: null },
  agent: {
    wave: "triangle",
    detune: 9,
    attack: 0.006,
    release: 0.07,
    noise: false,
    filter: { kind: "highpass", of: 1.6 },
  },
  system: {
    wave: "sine",
    detune: 0,
    attack: 0.001,
    release: 0.02,
    noise: true,
    filter: { kind: "bandpass", of: 1 },
  },
};

type Melody = { steps: number[]; apart: number; seconds: number; gain: number; glide?: number };

const MELODIES: Record<Cue, Melody> = {
  message: { steps: [0, SMALL], apart: 0.055, seconds: 0.07, gain: 0.34 },

  mention: { steps: [0, WIDE], apart: 0.06, seconds: 0.075, gain: 0.62 },

  agentDone: { steps: [4, 0], apart: 0.055, seconds: 0.07, gain: 0.3 },

  agentNeedsYou: { steps: [0, 4, WIDE], apart: 0.042, seconds: 0.05, gain: 0.56 },

  ringing: { steps: [WIDE, 4], apart: 0.2, seconds: 0.16, gain: 0.72 },

  youJoined: { steps: [0], apart: 0.05, seconds: 0.11, gain: 0.46, glide: SMALL },
  somebodyJoined: { steps: [0, 4], apart: 0.055, seconds: 0.07, gain: 0.4 },
  somebodyLeft: { steps: [4, 0], apart: 0.055, seconds: 0.07, gain: 0.4 },
  callEnded: { steps: [WIDE, 4, 0], apart: 0.045, seconds: 0.055, gain: 0.42 },

  recordingStarted: { steps: [12], apart: 0.05, seconds: 0.02, gain: 0.12 },

  sendFailed: { steps: [-12, -12], apart: 0.055, seconds: 0.03, gain: 0.5 },

  summary: { steps: [0, SMALL, 4], apart: 0.045, seconds: 0.055, gain: 0.32 },
};

export function planFor(cue: Cue, voice: Voice, loudness = 1): Plan {
  const melody = MELODIES[cue];
  const shape = SHAPES[voice];
  const gain = melody.gain * loudness;
  const notes: Note[] = melody.steps.map((semitones, index) => {
    const hz = step(semitones);
    const note: Note = { at: index * melody.apart, seconds: melody.seconds, hz, gain };
    return melody.glide === undefined ? note : { ...note, toHz: step(semitones + melody.glide) };
  });
  return { cue, voice, shape, notes };
}

export function lengthOf(plan: Plan): number {
  const last = plan.notes.reduce((longest, note) => Math.max(longest, note.at + note.seconds), 0);
  return last + plan.shape.release;
}
